import { SidebarProvider, SidebarInset, SidebarTrigger } from 'src/components/ui/sidebar';
import { TourProvider, TourWelcomeDialog, APP_TOUR } from 'src/components/tour';
import { InstallBanner } from 'src/components/install-banner';
import { ErrorBoundary } from 'src/components/error';
import { BootstrapProgressBanner } from 'src/components/bootstrap-progress-banner';
import { useInstallHealth } from 'src/hooks/useInstallHealth';
import { useBootstrapProgress } from 'src/hooks/useBootstrapProgress';
import { useAppBootstrap } from 'src/contexts/app-bootstrap-context';
import { isActiveInstallPhase, isTerminalInstallPhase, withinHours } from 'src/utils/install-phase';
import { AppSidebar } from './app-sidebar';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

// Module-level so the `tours` prop TourProvider receives keeps a stable
// reference across renders (see TourProvider's polling effect).
const APP_TOURS = [APP_TOUR];

/**
 * AppLayout - Main application layout with shadcn sidebar
 *
 * Features:
 * - Self-contained sidebar with company switcher (header) and user menu (footer)
 * - Collapsible sidebar with icon-only mode (toggle in sidebar footer)
 * - Keyboard shortcut (Cmd+B / Ctrl+B) to toggle
 * - Mobile-responsive drawer with floating trigger
 * - First-install banner during initial Shopify data load (precedes the
 *   tier-promotion bootstrap banner)
 */
export default function AppLayout({ children }: Props) {
  const { installHealth } = useInstallHealth();

  const { selectedCompany } = useAppBootstrap();
  const { bootstrapProgress } = useBootstrapProgress({
    companyId: selectedCompany?.id,
  });

  // The first-install banner shows while an install is in flight, or for the
  // 24h window after it completes. It takes precedence over the bootstrap
  // tier-promotion banner — a first install is the merchant's earliest state.
  const phase = installHealth?.installPhase ?? null;
  const shouldShowInstallBanner =
    phase != null &&
    (isActiveInstallPhase(phase) ||
      isTerminalInstallPhase(phase) ||
      (phase === 'FULL_READY' && withinHours(installHealth?.phaseEnteredAt, 24)));

  const shouldShowBootstrapBanner =
    !shouldShowInstallBanner && bootstrapProgress?.phase === 'building';

  return (
    <SidebarProvider>
      <TourProvider tours={APP_TOURS}>
        <TourWelcomeDialog />
        <AppSidebar />
        <SidebarInset>
          {/* Mobile-only header with sidebar trigger (hidden on desktop) */}
          <header className="flex h-12 shrink-0 items-center gap-2 bg-background px-4 md:hidden">
            <SidebarTrigger className="-ml-1" />
          </header>

          {/* First-install data-loading banner */}
          {shouldShowInstallBanner && <InstallBanner />}

          {/* BUILDING-zero-state banner — shown while Tier 1 promotion is in
              progress, only when no install banner is showing. */}
          {shouldShowBootstrapBanner && (
            <BootstrapProgressBanner bootstrapProgress={bootstrapProgress} />
          )}

          {/* Main content area - constrained to prevent bleeding into sidebar */}
          <main className="flex min-w-0 flex-1 flex-col gap-4 overflow-auto p-4 md:p-6">
            <ErrorBoundary>{children}</ErrorBoundary>
          </main>
        </SidebarInset>
      </TourProvider>
    </SidebarProvider>
  );
}
